"use client";

import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from "react-i18next";
import axiosInstance from '@/api/axios';

ChartJS.register(ArcElement, Tooltip, Legend);

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

export function WasteImpactCard({ bins = [], period = 'year', year, customDateRange }) {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
    const { t, i18n } = useTranslation();

    const customDateRangeKey = (() => {
        const r = customDateRange?.[0];
        if (!r?.startDate || !r?.endDate) return null;
        const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
        const end   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
        return `${start}__${end}`;
    })();

    const isCustom = Boolean(customDateRangeKey);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const params = { bins };

                if (isCustom) {
                    const r = customDateRange[0];
                    params.start_date = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
                    params.end_date   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
                } else {
                    params.period = period;
                    if (period === 'year' && year) params.year = year;
                }

                const response = await axiosInstance.get('/waste_impact/', { params });
                setData(response.data);
            } catch (err) {
                setError(err.message || t('wasteImpact.error'));
                console.error("Error fetching waste impact data:", err);
            } finally {
                setLoading(false);
            }
        };

        if (bins.length > 0) fetchData();
    }, [bins, period, year, customDateRangeKey]);

    // Date range label for header
    const getRangeLabel = () => {
        if (!data?.start_date || !data?.end_date) return null;
        const fmt = (iso) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
        if (isCustom) return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
        switch (period) {
            case 'day':   return fmt(data.start_date);
            case 'month': return new Date(data.start_date).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
            case 'year':  return year?.toString();
            default:      return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
        }
    };

    if (loading) return (
        <div className="grid grid-cols-subgrid lg:col-span-2 gap-1 sm:gap-4 items-start">
            <div className="lg:col-span-2 sm:rounded-lg bg-muted/50 p-2">
                <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                <div className="aspect-[4/3] md:aspect-[16/9] flex flex-col items-center justify-center">
                    <p>{t('wasteImpact.loading')}</p>
                </div>
            </div>
        </div>
    );

    if (error) return (
        <div className="grid grid-cols-subgrid lg:col-span-2 gap-1 sm:gap-4 items-start">
            <div className="lg:col-span-2 sm:rounded-lg bg-muted/50 p-2">
                <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                <div className="aspect-[4/3] md:aspect-[16/9] flex flex-col items-center justify-center p-4">
                    <p className="text-red-500 mb-4 text-center">{error}</p>
                </div>
            </div>
        </div>
    );

    if (!data) return (
        <div className="grid grid-cols-subgrid lg:col-span-2 gap-1 sm:gap-4 items-start">
            <div className="lg:col-span-2 sm:rounded-lg bg-muted/50 p-2">
                <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                <div className="aspect-[4/3] md:aspect-[16/9] flex items-center justify-center">
                    <p className="text-gray-500">{t('wasteImpact.noData')}</p>
                </div>
            </div>
        </div>
    );

    const wasteData = Object.entries(data.waste_streams).map(([wasteType, stats]) => ({
        name: wasteType, value: stats.waste, percentage: stats.percentage, unit: 'kg'
    }));

    const ghgData = Object.entries(data.waste_streams).map(([wasteType, stats]) => ({
        name: wasteType, value: stats.co2, percentage: (stats.co2 / data.total_co2) * 100, unit: 'kg CO₂e'
    }));

    const highestGHGStream = ghgData.reduce((max, item) =>
        item.percentage > max.percentage ? item : max, { name: '', percentage: 0 }
    );
    const wastePercentageForHighestGHG = wasteData.find(item => item.name === highestGHGStream.name)?.percentage || 0;
    const impactRatio = (highestGHGStream.percentage / wastePercentageForHighestGHG).toFixed(1);
    const isHighImpact = impactRatio > 1.5;

    const getTranslatedStreamName = (streamName) =>
        t(`wasteImpact.streams.${streamName.toLowerCase().trim()}`, streamName);

    const wasteChartData = {
        labels: wasteData.map(item => getTranslatedStreamName(item.name)),
        datasets: [{ data: wasteData.map(item => item.value), backgroundColor: COLORS, borderWidth: 0 }]
    };

    const ghgChartData = {
        labels: ghgData.map(item => getTranslatedStreamName(item.name)),
        datasets: [{ data: ghgData.map(item => item.value), backgroundColor: COLORS, borderWidth: 0 }]
    };

    const pieChartPercentagePlugin = {
        id: 'pieChartPercentage',
        afterDraw: (chart) => {
            const ctx = chart.ctx;
            const percentageData = chart.canvas.id.includes('waste')
                ? wasteData.map(item => item.percentage)
                : ghgData.map(item => item.percentage);

            chart.getDatasetMeta(0).data.forEach((element, index) => {
                const percent = percentageData[index].toFixed(1) + '%';
                const midAngle = (element.startAngle + element.endAngle) / 2;
                const radius = element.outerRadius * 1.2;
                const x = element.x + Math.cos(midAngle) * radius;
                const y = element.y + Math.sin(midAngle) * radius;
                const baseFontSize = windowWidth < 640 ? 12 : windowWidth < 768 ? 14 : 16;

                ctx.font = `bold ${baseFontSize}px Arial`;
                ctx.fillStyle = '#374151';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.shadowColor = 'rgba(255,255,255,0.9)';
                ctx.shadowBlur = 4;
                ctx.fillText(percent, x, y);
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
            });
        }
    };

    const makeChartOptions = (sourceData) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const item = sourceData[context.dataIndex];
                        return [
                            `${item.name.toUpperCase()}`,
                            `${t('wasteImpact.tooltip.amount')}: ${item.value.toFixed(1)} ${item.unit}`,
                            `${t('wasteImpact.tooltip.percentage')}: ${item.percentage.toFixed(1)}%`
                        ];
                    }
                }
            },
            legend: { display: false },
            datalabels: { display: false }
        },
        cutout: 0,
        radius: '70%'
    });

    return (
        <div className="grid grid-cols-subgrid lg:col-span-2 gap-1 sm:gap-4 items-start">
            <div className="lg:col-span-2 sm:rounded-lg bg-muted/50 p-2">
                <div className="flex items-center justify-between">
                    <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                    <div className="flex items-center gap-2">
                        {getRangeLabel() && (
                            <span className="text-xs text-muted-foreground">{getRangeLabel()}</span>
                        )}
                        <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${isHighImpact ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
                            {isHighImpact && <AlertTriangle className="w-3 h-3" />}
                            <span className="font-medium">{impactRatio}x</span>
                        </div>
                    </div>
                </div>

                <div className="aspect-[4/3] md:aspect-[16/9]">
                    <div className="flex flex-col h-full w-full gap-0">
                        <div className="px-2 py-2">
                            <div className="text-center">
                                <p className="text-xs sm:text-sm md:text-base lg:text-lg font-medium text-foreground leading-relaxed">
                                    <span className={isHighImpact ? 'text-destructive font-semibold' : 'text-primary font-semibold'}>
                                        {Math.round(wastePercentageForHighestGHG)}%
                                    </span>
                                    {' '}of your waste (<span className="font-semibold">{getTranslatedStreamName(highestGHGStream.name)}</span>) is responsible for{' '}
                                    <span className={isHighImpact ? 'text-destructive font-semibold' : 'text-primary font-semibold'}>
                                        {Math.round(highestGHGStream.percentage)}%
                                    </span>
                                    {' '}of your environmental CO₂
                                </p>
                            </div>
                        </div>

                        <div className="flex-1 grid grid-cols-2 gap-0 relative min-h-0">
                            <div className="flex flex-col h-full pt-0.5 min-h-0">
                                <h3 className="text-[0.65rem] sm:text-[0.75rem] md:text-[0.90rem] font-medium text-center text-muted-foreground">
                                    {t('wasteImpact.labels.volume').toUpperCase()}
                                </h3>
                                <div className="flex-1 min-h-0 relative">
                                    <Pie key={windowWidth} data={wasteChartData} options={makeChartOptions(wasteData)} plugins={[pieChartPercentagePlugin]} id="waste-chart" />
                                </div>
                            </div>

                            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-1/3 flex flex-col items-center z-10">
                                <div className="bg-white/80 dark:bg-gray-800/80 rounded-lg p-1 md:p-2 shadow-sm">
                                    <ul className="flex flex-col gap-0.5 md:gap-1">
                                        {wasteData.map((item, index) => (
                                            <li key={item.name} className="flex items-center gap-1 md:gap-2">
                                                <span className="w-2 h-2 md:w-3 md:h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                                                <span className="text-[0.6rem] md:text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    {getTranslatedStreamName(item.name).toUpperCase()}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="flex flex-col h-full pt-0.5 min-h-0">
                                <h3 className="text-[0.65rem] sm:text-[0.75rem] md:text-[0.90rem] font-medium text-center text-muted-foreground">
                                    {t('wasteImpact.labels.impact').toUpperCase()}
                                </h3>
                                <div className="flex-1 min-h-0 relative">
                                    <Pie key={windowWidth} data={ghgChartData} options={makeChartOptions(ghgData)} plugins={[pieChartPercentagePlugin]} id="ghg-chart" />
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-0.5">
                            <p className="text-[0.65rem] sm:text-xs font-medium text-foreground">
                                <span className={isHighImpact ? 'text-destructive' : 'text-primary'}>{t('wasteImpact.focus')}:</span>
                                {' '}{t('wasteImpact.reduce')} {getTranslatedStreamName(highestGHGStream.name)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}